/**
 * 日管控模块 Vuex Store
 * 用于缓存模板数据和用户列表，减少接口调用
 */

const CACHE_DURATION = 30 * 60 * 1000; // 缓存30分钟

const state = {
  // 模板数据缓存 { key: { data: {}, timestamp: number } }
  templateCache: {},
  // 用户列表缓存
  userList: [],
  userListTimestamp: 0,
  // 日管控模板列表缓存
  templateList: [],
  templateListTimestamp: 0,
  // 项目列表缓存
  itemList: [],
  itemListTimestamp: 0,
  // 子项目缓存 { parentId: { data: [], timestamp: number } }
  childItemsCache: {},
  // 处理人列表缓存
  handlerList: [],
  handlerListTimestamp: 0,
};

const getters = {
  /**
   * 根据 key 获取缓存的模板数据
   * @param {string} key - 模板key (templateNo 或 'default')
   * @returns {object|null} - 缓存的模板数据，如果过期则返回 null
   */
  getTemplateByKey: (state) => (key) => {
    const cached = state.templateCache[key];
    if (!cached) return null;
    
    const now = Date.now();
    const isExpired = now - cached.timestamp > CACHE_DURATION;
    
    if (isExpired) {
      console.log(`模板缓存已过期: ${key}`);
      return null;
    }
    
    return cached.data;
  },

  /**
   * 获取缓存的用户列表
   * @returns {array} - 用户列表，如果过期则返回空数组
   */
  getUserList: (state) => {
    if (!state.userList || state.userList.length === 0) return [];
    
    const now = Date.now();
    const isExpired = now - state.userListTimestamp > CACHE_DURATION;
    
    if (isExpired) {
      console.log('用户列表缓存已过期');
      return [];
    }
    
    return state.userList;
  },

  /**
   * 获取所有缓存的模板 keys
   */
  getCachedTemplateKeys: (state) => {
    return Object.keys(state.templateCache);
  },

  /**
   * 获取缓存的模板列表
   * @returns {array} - 模板列表，如果过期则返回空数组
   */
  getTemplateList: (state) => {
    if (!state.templateList || state.templateList.length === 0) return [];
    
    const now = Date.now();
    const isExpired = now - state.templateListTimestamp > CACHE_DURATION;
    
    if (isExpired) {
      console.log('模板列表缓存已过期');
      return [];
    }
    
    return state.templateList;
  },

  /**
   * 获取缓存的项目列表
   * @returns {array} - 项目列表，如果过期则返回空数组
   */
  getItemList: (state) => {
    if (!state.itemList || state.itemList.length === 0) return [];
    
    const now = Date.now();
    const isExpired = now - state.itemListTimestamp > CACHE_DURATION;
    
    if (isExpired) {
      console.log('项目列表缓存已过期');
      return [];
    }
    
    return state.itemList;
  },

  /**
   * 根据父项目ID获取子项目列表
   * @param {number} parentId - 父项目ID
   * @returns {array} - 子项目列表，如果过期则返回空数组
   */
  getChildItemsByParentId: (state) => (parentId) => {
    const cached = state.childItemsCache[parentId];
    if (!cached) return [];
    
    const now = Date.now();
    const isExpired = now - cached.timestamp > CACHE_DURATION;
    
    if (isExpired) {
      console.log(`子项目缓存已过期: parentId=${parentId}`);
      return [];
    }
    
    return cached.data;
  },

  /**
   * 获取缓存的处理人列表
   * @returns {array} - 处理人列表，如果过期则返回空数组
   */
  getHandlerList: (state) => {
    if (!state.handlerList || state.handlerList.length === 0) return [];
    
    const now = Date.now();
    const isExpired = now - state.handlerListTimestamp > CACHE_DURATION;
    
    if (isExpired) {
      console.log('处理人列表缓存已过期');
      return [];
    }
    
    return state.handlerList;
  },
};

const mutations = {
  /**
   * 设置模板缓存
   * @param {string} key - 模板key
   * @param {object} data - 模板数据
   */
  setTemplateCache(state, { key, data }) {
    state.templateCache = {
      ...state.templateCache,
      [key]: {
        data,
        timestamp: Date.now(),
      },
    };
    console.log(`模板数据已缓存: ${key}`);
  },

  /**
   * 设置用户列表缓存
   * @param {array} userList - 用户列表
   */
  setUserList(state, userList) {
    state.userList = userList;
    state.userListTimestamp = Date.now();
    console.log(`用户列表已缓存，共 ${userList.length} 个用户`);
  },

  /**
   * 清除指定模板缓存
   * @param {string} key - 模板key
   */
  clearTemplateCache(state, key) {
    if (key) {
      delete state.templateCache[key];
      console.log(`已清除模板缓存: ${key}`);
    } else {
      state.templateCache = {};
      console.log('已清除所有模板缓存');
    }
  },

  /**
   * 清除用户列表缓存
   */
  clearUserList(state) {
    state.userList = [];
    state.userListTimestamp = 0;
    console.log('已清除用户列表缓存');
  },

  /**
   * 清除所有缓存
   */
  clearAllCache(state) {
    state.templateCache = {};
    state.userList = [];
    state.userListTimestamp = 0;
    state.templateList = [];
    state.templateListTimestamp = 0;
    state.itemList = [];
    state.itemListTimestamp = 0;
    state.childItemsCache = {};
    state.handlerList = [];
    state.handlerListTimestamp = 0;
    console.log('已清除所有日管控缓存');
  },

  /**
   * 设置模板列表缓存
   * @param {array} templateList - 模板列表
   */
  setTemplateList(state, templateList) {
    state.templateList = templateList;
    state.templateListTimestamp = Date.now();
    console.log(`模板列表已缓存，共 ${templateList.length} 个模板`);
  },

  /**
   * 设置项目列表缓存
   * @param {array} itemList - 项目列表
   */
  setItemList(state, itemList) {
    state.itemList = itemList;
    state.itemListTimestamp = Date.now();
    console.log(`项目列表已缓存，共 ${itemList.length} 个项目`);
  },

  /**
   * 设置子项目缓存
   * @param {object} payload - { parentId: number, data: array }
   */
  setChildItems(state, { parentId, data }) {
    state.childItemsCache = {
      ...state.childItemsCache,
      [parentId]: {
        data,
        timestamp: Date.now(),
      },
    };
    console.log(`子项目已缓存: parentId=${parentId}, 共 ${data.length} 项`);
  },

  /**
   * 设置处理人列表缓存
   * @param {array} handlerList - 处理人列表
   */
  setHandlerList(state, handlerList) {
    state.handlerList = handlerList;
    state.handlerListTimestamp = Date.now();
    console.log(`处理人列表已缓存，共 ${handlerList.length} 个处理人`);
  },

  /**
   * 清除模板列表缓存
   */
  clearTemplateList(state) {
    state.templateList = [];
    state.templateListTimestamp = 0;
    console.log('已清除模板列表缓存');
  },

  /**
   * 清除项目列表缓存
   */
  clearItemList(state) {
    state.itemList = [];
    state.itemListTimestamp = 0;
    console.log('已清除项目列表缓存');
  },

  /**
   * 清除指定父项目的子项目缓存
   * @param {number} parentId - 父项目ID
   */
  clearChildItems(state, parentId) {
    if (parentId) {
      delete state.childItemsCache[parentId];
      console.log(`已清除子项目缓存: parentId=${parentId}`);
    } else {
      state.childItemsCache = {};
      console.log('已清除所有子项目缓存');
    }
  },

  /**
   * 清除处理人列表缓存
   */
  clearHandlerList(state) {
    state.handlerList = [];
    state.handlerListTimestamp = 0;
    console.log('已清除处理人列表缓存');
  },
};

const actions = {
  /**
   * 主动刷新模板缓存
   */
  async refreshTemplateCache({ commit }, { key, fetchFn }) {
    try {
      console.log(`正在刷新模板缓存: ${key}`);
      const data = await fetchFn();
      commit('setTemplateCache', { key, data });
      return data;
    } catch (error) {
      console.error('刷新模板缓存失败:', error);
      throw error;
    }
  },

  /**
   * 主动刷新用户列表缓存
   */
  async refreshUserList({ commit }, fetchFn) {
    try {
      console.log('正在刷新用户列表缓存');
      const data = await fetchFn();
      commit('setUserList', data);
      return data;
    } catch (error) {
      console.error('刷新用户列表缓存失败:', error);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
